import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useHistory } from '../components/hooks/useHistory';
import { IDossier, ISuggestion } from '../components/widgets/types';
import { apiClient } from '../../utils/apiClient';
import { useWebSocket } from '../../context/WebSocketContext';
import { generateObjectIdHex } from '../../utils/objectId';
import { restoreDossierVersion } from '../../services/dossierHistoryService';
import { DossierContextValue, EditMode, PreviewVersion } from './types';

const DossierContext = createContext<DossierContextValue | null>(null);

/**
 * Hook accessor — strict (jette si pas wrappé dans un `DossierProvider`).
 *
 * Ce contrat strict est volontaire : tout le sous-arbre `DossierEditor`
 * dépend du context, et un consumer hors arbre est un bug à signaler
 * tôt plutôt qu'un fallback silencieux.
 */
export function useDossier(): DossierContextValue {
  const ctx = useContext(DossierContext);
  if (ctx === null) {
    throw new Error('useDossier doit être utilisé à l’intérieur d’un <DossierProvider>');
  }
  return ctx;
}

interface DossierProviderProps {
  dossierId: string;
  initialDossier: IDossier;
  initialLastSaved: Date;
  userRole: string | null;
  children: React.ReactNode;
}

export const DossierProvider: React.FC<DossierProviderProps> = ({
  dossierId,
  initialDossier,
  initialLastSaved,
  userRole,
  children,
}) => {
  // -------------------------------------------------------------------------
  // Document + historique
  // -------------------------------------------------------------------------
  const {
    state: dossier,
    setState: setDossierInternal,
    undo,
    redo,
    canUndo,
    canRedo,
  } = useHistory<IDossier>(initialDossier, {
    maxHistorySize: 100,
    debounceMs: 500,
  });

  const [currentChapterIndex, setCurrentChapterIndex] = useState(0);

  const updateDossier = useCallback(
    (recipe: (draft: IDossier) => void) => {
      setDossierInternal(draft => {
        recipe(draft);
      });
    },
    [setDossierInternal]
  );

  const replaceDossier = useCallback(
    (next: IDossier) => {
      setDossierInternal(next);
    },
    [setDossierInternal]
  );

  // -------------------------------------------------------------------------
  // Mode d'édition + preview
  // -------------------------------------------------------------------------
  const [editMode, setEditMode] = useState<EditMode>('reader');
  const [previewVersion, setPreviewVersion] = useState<PreviewVersion | null>(null);
  const [isRestoringPreview, setIsRestoringPreview] = useState(false);

  const enterPreview = useCallback((versionNumber: number, snapshot: IDossier) => {
    setPreviewVersion({ versionNumber, snapshot });
  }, []);

  const exitPreview = useCallback(() => {
    setPreviewVersion(null);
  }, []);

  const restoreFromPreview = useCallback(async () => {
    if (!previewVersion) return;
    setIsRestoringPreview(true);
    try {
      const result = await restoreDossierVersion(dossierId, previewVersion.versionNumber);
      if (result?.dossier) {
        replaceDossier(result.dossier as IDossier);
        setPreviewVersion(null);
      }
    } finally {
      setIsRestoringPreview(false);
    }
  }, [dossierId, previewVersion, replaceDossier]);

  // -------------------------------------------------------------------------
  // Auto-save & dirty tracking
  // -------------------------------------------------------------------------
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(initialLastSaved);

  // Ref pour éviter une boucle infinie : on veut détecter les
  // mutations de dossier sans re-créer save() à chaque touche.
  const dossierRef = useRef(dossier);
  useEffect(() => {
    dossierRef.current = dossier;
  }, [dossier]);

  // Marquer hasUnsavedChanges quand le dossier mute après le mount
  // initial. Le tout premier render fixe le baseline sans marquer dirty.
  const baselineRef = useRef<IDossier | null>(null);
  useEffect(() => {
    if (baselineRef.current === null) {
      baselineRef.current = dossier;
      return;
    }
    if (baselineRef.current !== dossier) {
      baselineRef.current = dossier;
      setHasUnsavedChanges(true);
    }
  }, [dossier]);

  const save = useCallback(async () => {
    const current = dossierRef.current;
    if (!current || !current.content || !Array.isArray(current.content)) return;
    if (isSaving) return;

    setIsSaving(true);
    try {
      const normalizedContent = current.content.map(chapter => ({
        ...chapter,
        _id: !chapter._id || chapter._id.length !== 24 ? generateObjectIdHex() : chapter._id,
        sections: (chapter.sections || []).map(section => ({
          ...section,
          _id: !section._id || section._id.length !== 24 ? generateObjectIdHex() : section._id,
          blocks: (section.blocks || []).map(block => ({
            ...block,
            _id: !block._id || block._id.length !== 24 ? generateObjectIdHex() : block._id,
          })),
        })),
      }));

      const payload = {
        ...current,
        updatedAt: new Date().toISOString(),
        content: normalizedContent,
      };

      const response = await apiClient(`/dossiers/${dossierId}/light`, {
        method: 'PUT',
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`Erreur sauvegarde HTTP ${response.status}`);
      }

      const data = await response.json();
      setLastSaved(data.updatedAt ? new Date(data.updatedAt) : new Date());
      setHasUnsavedChanges(false);
    } catch (error) {
      console.error('DossierProvider.save: erreur', error);
    } finally {
      setIsSaving(false);
    }
  }, [dossierId, isSaving]);

  // -------------------------------------------------------------------------
  // Suggestions
  // -------------------------------------------------------------------------
  const [suggestions, setSuggestions] = useState<ISuggestion[]>([]);
  const { socket } = useWebSocket();

  useEffect(() => {
    let cancelled = false;
    const fetchSuggestions = async () => {
      try {
        const response = await apiClient(`/suggestions/dossier/${dossierId}`);
        if (!response.ok) return;
        const data: ISuggestion[] = await response.json();
        if (cancelled) return;
        setSuggestions(data.filter(s => s.status === 'pending'));
      } catch (error) {
        console.error('DossierProvider.fetchSuggestions: erreur', error);
      }
    };
    void fetchSuggestions();
    return () => {
      cancelled = true;
    };
  }, [dossierId]);

  useEffect(() => {
    if (!socket) return;
    const handler = (incoming: ISuggestion) => {
      if (incoming.dossierId === dossierId && incoming.status === 'pending') {
        setSuggestions(prev => [...prev, incoming]);
      }
    };
    socket.on('newSuggestion', handler);
    return () => {
      socket.off('newSuggestion', handler);
    };
  }, [socket, dossierId]);

  const acceptSuggestion = useCallback(
    async (suggestion: ISuggestion) => {
      try {
        const response = await apiClient(`/suggestions/${suggestion._id}/status`, {
          method: 'PUT',
          body: JSON.stringify({ status: 'approved' }),
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        // Forcer un nouveau snapshot pour propager la modification
        // induite côté backend (re-render des widgets).
        updateDossier(draft => {
          draft.updatedAt = new Date().toISOString();
        });
        setSuggestions(prev => prev.filter(s => s._id !== suggestion._id));
      } catch (error) {
        console.error('DossierProvider.acceptSuggestion: erreur', error);
      }
    },
    [updateDossier]
  );

  const rejectSuggestion = useCallback(async (suggestion: ISuggestion) => {
    try {
      const response = await apiClient(`/suggestions/${suggestion._id}/status`, {
        method: 'PUT',
        body: JSON.stringify({ status: 'denied' }),
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      setSuggestions(prev => prev.filter(s => s._id !== suggestion._id));
    } catch (error) {
      console.error('DossierProvider.rejectSuggestion: erreur', error);
    }
  }, []);

  const addSuggestion = useCallback((suggestion: ISuggestion) => {
    setSuggestions(prev => [...prev, suggestion]);
  }, []);

  // -------------------------------------------------------------------------
  // Dérivés
  // -------------------------------------------------------------------------
  const isPreviewMode = previewVersion !== null;
  const isEditing = editMode === 'editor' && !isPreviewMode;
  const currentChapter = useMemo(() => {
    if (isPreviewMode && previewVersion?.snapshot?.content?.length) {
      return (
        previewVersion.snapshot.content[currentChapterIndex] ?? previewVersion.snapshot.content[0]
      );
    }
    if (!dossier?.content?.length) return null;
    return dossier.content[currentChapterIndex] ?? dossier.content[0];
  }, [dossier, currentChapterIndex, isPreviewMode, previewVersion]);

  // -------------------------------------------------------------------------
  // Value mémorisée
  // -------------------------------------------------------------------------
  const value = useMemo<DossierContextValue>(
    () => ({
      dossierId,
      userRole,
      dossier,
      updateDossier,
      replaceDossier,
      currentChapterIndex,
      setCurrentChapterIndex,
      currentChapter,
      editMode,
      setEditMode,
      isEditing,
      hasUnsavedChanges,
      isSaving,
      lastSaved,
      save,
      undo,
      redo,
      canUndo,
      canRedo,
      suggestions,
      acceptSuggestion,
      rejectSuggestion,
      addSuggestion,
      previewVersion,
      enterPreview,
      exitPreview,
      restoreFromPreview,
      isRestoringPreview,
      isPreviewMode,
    }),
    [
      dossierId,
      userRole,
      dossier,
      updateDossier,
      replaceDossier,
      currentChapterIndex,
      currentChapter,
      editMode,
      isEditing,
      hasUnsavedChanges,
      isSaving,
      lastSaved,
      save,
      undo,
      redo,
      canUndo,
      canRedo,
      suggestions,
      acceptSuggestion,
      rejectSuggestion,
      addSuggestion,
      previewVersion,
      enterPreview,
      exitPreview,
      restoreFromPreview,
      isRestoringPreview,
      isPreviewMode,
    ]
  );

  return <DossierContext.Provider value={value}>{children}</DossierContext.Provider>;
};
